import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MotifIcon, MotifButton } from "@/libs/@ey-xd/motif-react";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";

import "./Attributes.css";
import CreateAttributes from "./CreateAttributes";
import ReviewAttribute from "./ReviewAttributes";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {
  useAttributeByIdQuery,
  useCreateAttributeMutation,
  useListAttributesQuery,
  useUpdateAttributeMutation,
} from "@/services/customAtrributes/customAttributesReducer";
import { SetupStage, AttributeType } from "./type";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import PageTitle from "@/components/Form/PageTitle/PageTitle";
import {
  CANCEL_BUTTON_VARIANT,
  SAVE_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import {
  MotifErrorMessage,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { ActiveTabsOrder } from "@/menuData";
import { maxPageSize } from "@/Constant";
import { setAttribute } from "./AttributesSlice";
export default function AttributeSetup() {
  const navigate = useNavigate();
  const { caseId, attributeId } = useParams();
  const [searchParms] = useSearchParams();
  const { t } = useTranslation();
  const { caseName } = useSelector((state: any) => state.case);
  const attributeData = useSelector((state: any) => state.attribute);
  const getEntityType =
    searchParms?.get("entityType") || attributeData?.entityType;
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      caseId: caseId,
      entityType: getEntityType,
      PageNumber: 1,
      PageSize: maxPageSize,
    };
    return value;
  }, [caseId, getEntityType]);
  const paginationObj = getPaginationObj;
  const {
    data,
    error: attributesListError,
    isLoading: attributesListLoading,
  } = useListAttributesQuery(paginationObj);

  let [intialAttributeData, setIntialAttributeData] = useState<any>(null);
  const {
    data: attribiteApiData,
    isFetching,
    isError,
  } = useAttributeByIdQuery(attributeId, {
    skip: !attributeId,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (attribiteApiData && !isFetching) {
      const attribute = {
        ...attribiteApiData,
        metaDataJSON: JSON.parse(attribiteApiData?.metadataJSON),
        id: attributeId,
      };
      dispatch(setAttribute(attribute));
      setIntialAttributeData(attribute);
    }
  }, [attribiteApiData, isFetching, dispatch, attributeId]);
  const [setupStage, setSetupStage] = useState(SetupStage.Create);
  const [createAttribute] = useCreateAttributeMutation();
  const [updateAttribute] = useUpdateAttributeMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscardVisible, setIsDiscardVisible] = useState(false);
  const [checkMinMaxRange, setCheckMinMaxRange] = useState(false);
  const [tosatMessage, setToastMessage] = useState<any>(null);
  function checkMinValueIsLessThanMaxValue() {
    let flag = true;
    if (attributeData?.attributeType === 2) {
      let minValue = attributeData?.metaDataJSON?.minimum;
      let maxValue = attributeData?.metaDataJSON?.maximum;
      if (minValue > maxValue) {
        flag = false;
        setCheckMinMaxRange(true);
        setToastMessage("pages.configuration.attributes.minMaxRangeError");
        scrollToTop();
      }
    }

    return flag;
  }
  function scrollToTop() {
    window.scrollTo(0, 0);
    return true;
  }
  const [errorMessage, setErrorMessage] = useState("");
  function checkAttributeNameExist() {
    let flag = false;
    if (
      attributeData.id &&
      intialAttributeData &&
      attributeData.name === intialAttributeData?.name
    ) {
      return false;
    }
    if (data?.results && data?.results.length > 0) {
      const attributeNameExist = data?.results?.filter((x: any) => {
        return (
          x.name === attributeData.name &&
          x.entityType?.toString() === getEntityType?.toString()
        );
      });
      if (attributeNameExist.length > 0) {
        flag = true;
      }
    }
    return flag;
  }
  function handleTrimPrickList(data: any) {
    let pickListArray = data?.picklistDefinitions?.map((x: any) => {
      let option = { ...x };
      option.name = option.name.trim();
      return option;
    });

    return pickListArray;
  }
  const handleSubmit = async () => {
    if (checkAttributeNameExist()) {
      scrollToTop();
      setToastMessage("pages.configuration.attributes.sameAttributeName");

      setCheckMinMaxRange(true);
      return;
    }
    if (checkMinValueIsLessThanMaxValue()) {
      setCheckMinMaxRange(false);

      if (setupStage === SetupStage.Create) {
        setSetupStage(SetupStage.Review);
      } else {
        setIsLoading(true);
        if (attributeData.id) {
          let metaJSON = { ...attributeData.metaDataJSON };
          if (attributeData.attributeType === 2) {
            metaJSON.minimum = metaJSON.minimum
              ? Number(metaJSON.minimum)
              : null;
            metaJSON.maximum = metaJSON.maximum
              ? Number(metaJSON.maximum)
              : null;
          }
          const requestBody = {
            ...attributeData,
            name: attributeData.name.trim(),
            nameFrench: attributeData.nameFrench?.trim(),
            description: attributeData.description?.trim(),
            descriptionFrench: attributeData.descriptionFrench?.trim(),
            metaDataJSON: JSON.stringify(metaJSON),
          };
          if (attributeData?.picklistDefinitions?.length > 0) {
            requestBody.picklistDefinitions =
              handleTrimPrickList(attributeData);
          }
          dispatch(setDiscardEditMode({ editMode: false }));
          await updateAttribute(requestBody).then((response: any) => {
            if (response.hasOwnProperty("data")) {
              setIsLoading(false);
              navigate(
                `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
              );
            } else {
              setIsLoading(false);
              //@ts-ignore
              setErrorMessage(
                response.error?.data?.message ||
                  t("global.globalNotifications.unExpectedError")
              );
            }
          });
        } else {
          let metaJSON = { ...attributeData.metaDataJSON };
          if (attributeData.attributeType === 2) {
            metaJSON.minimum = metaJSON.minimum
              ? Number(metaJSON.minimum)
              : null;
            metaJSON.maximum = metaJSON.maximum
              ? Number(metaJSON.maximum)
              : null;
          }
          const requestBody = {
            ...attributeData,
            name: attributeData.name.trim(),
            nameFrench: attributeData.nameFrench?.trim(),
            description: attributeData.description?.trim(),
            descriptionFrench: attributeData.descriptionFrench?.trim(),
            metaDataJSON: JSON.stringify(metaJSON),
            caseId: caseId,
          };
          if (attributeData?.picklistDefinitions?.length > 0) {
            requestBody.picklistDefinitions =
              handleTrimPrickList(attributeData);
          }
          delete requestBody.id;
          dispatch(setDiscardEditMode({ editMode: false }));
          await createAttribute(requestBody).then((response: any) => {
            if (response.hasOwnProperty("data")) {
              setIsLoading(false);
              navigate(
                `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
              );
            } else {
              setIsLoading(false);
              //@ts-ignore
              setErrorMessage(
                response.error?.data?.message ||
                  t("global.globalNotifications.unExpectedError")
              );
            }
          });
        }
      }
    }
  };

  const isDisabled = () => {
    const emptyOptions = attributeData?.picklistDefinitions?.filter(
      (x: any) => {
        return x.name === "";
      }
    );
    return (
      !attributeData.name ||
      !attributeData.attributeType ||
      (attributeData.attributeType === AttributeType.Number &&
        (!attributeData.metaDataJSON ||
          (!attributeData.metaDataJSON.minimum &&
            attributeData.metaDataJSON.minimum !== 0) ||
          (!attributeData.metaDataJSON.maximum &&
            attributeData.metaDataJSON.maximum !== 0))) ||
      (attributeData.attributeType === AttributeType.FreeText &&
        (!attributeData.metaDataJSON.format ||
          (attributeData.metaDataJSON.format === "None" &&
            !attributeData.metaDataJSON.maxCharacter) ||
          (attributeData.metaDataJSON.format === "Regex" &&
            (!attributeData.metaDataJSON.regexPattern ||
              !attributeData.metaDataJSON.warningMessage)))) ||
      ((attributeData.attributeType === AttributeType.SingleSelect ||
        attributeData.attributeType === AttributeType.MutliSelect) &&
        (attributeData.picklistDefinitions.length === 0 ||
          emptyOptions.length !== 0))
    );
  };

  const handleBackButton = () => {
    if (setupStage === SetupStage.Create) {
      if (disacardTrig) {
        dispatch(setDiscardEditMode({ editMode: false }));

        setIsDiscardVisible(true);
      } else {
        navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`);
      }
    } else {
      setSetupStage(SetupStage.Create);
    }
  };
  let disacardTrig =
    JSON.stringify(intialAttributeData) !== JSON.stringify(attributeData);
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: disacardTrig }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, disacardTrig]);
  if (
    attributesListLoading ||
    isFetching ||
    (!attributeData?.name && attributeId)
  ) {
    return <MotifProgressLoader></MotifProgressLoader>;
  }
  if (attributesListError || isError) {
    return (
      <MotifErrorMessage>
        {t("global.globalNotifications.wentWrong")}
      </MotifErrorMessage>
    );
  }
  return (
    <div className="attribute-setup-container pad-h-60 pad-v-60">
      <div className="attributes-back-button" onClick={handleBackButton}>
        <Breadcrumb
          items={
            attributeData?.id
              ? [
                  {
                    text: caseName,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    blockNavigation: true,
                  },
                  {
                    text: attributeData?.name,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,

                    disabled: true,
                  },
                ]
              : [
                  {
                    text: caseName,
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    url: `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`,
                    blockNavigation: !(setupStage === SetupStage.Create),
                  },
                  {
                    text: t(
                      "pages.configuration.attributes.createAttributeButton"
                    ),
                    icon: <MotifIcon src={IconoirArrowLeft} />,
                    url: `/`,
                    disabled: true,
                    blockNavigation: true,
                  },
                ]
          }
          ariaLabel="Breadcrumb navigation"
        />
      </div>
      <PageTitle title={t("pages.configuration.attributes.attributeSetup")} />
      {checkMinMaxRange && (
        <MotifToast onClose={() => setCheckMinMaxRange(false)} variant="error">
          {t(tosatMessage)}
        </MotifToast>
      )}

      {errorMessage && errorMessage !== "" && (
        <NotificationGrid show={true} message={errorMessage} type={"error"} />
      )}

      {setupStage === SetupStage.Create ? (
        <CreateAttributes formType={attributeData.id ? "edit" : "new"} />
      ) : (
        <ReviewAttribute />
      )}

      <div className="attributes-footer-buttons button-container sticky-footer">
        <MotifButton
          onClick={() => {
            if (disacardTrig) {
              dispatch(setDiscardEditMode({ editMode: false }));

              setIsDiscardVisible(true);
            } else {
              navigate(
                `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
              );
            }
          }}
          size="small"
          variant={CANCEL_BUTTON_VARIANT}
        >
          {t("pages.configuration.attributes.cancelButton")}
        </MotifButton>
        <MotifButton
          onClick={handleSubmit}
          size="small"
          variant={SAVE_BUTTON_VARIANT}
          type={isLoading ? "loader" : "submit"}
          disabled={isDisabled() || isLoading}
        >
          {setupStage === SetupStage.Create ? (
            <span>{t("pages.configuration.attributes.reviewButton")}</span>
          ) : (
            <span>{t("pages.configuration.attributes.submitButton")}</span>
          )}
        </MotifButton>
      </div>
      <ConfirmationModal
        isVisible={isDiscardVisible}
        setIsVisible={setIsDiscardVisible}
        cancelBtnApproveHandler={() => {
          navigate(
            `/case/${caseId}?activeTab=${ActiveTabsOrder.CONFIGURATION}`
          );
        }}
        cancelBtnRejectHandler={() => {
          setIsDiscardVisible(false);
          dispatch(setDiscardEditMode({ editMode: true }));
        }}
      />
    </div>
  );
}
